<script setup lang="ts">
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Uppercase label, e.g. "Columns". */
  label: {
    type: String,
    required: true,
  },
  /** The value; use the default slot instead for anything richer than text. */
  value: {
    type: String,
    default: undefined,
  },
  /** Nothing chosen yet — renders the value muted. */
  empty: {
    type: Boolean,
    default: false,
  },
  /** Clicking jumps back to the step that sets this. */
  clickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ click: [] }>();

const border = gaiaNeutral.borderCard;
const borderHover = gaiaNeutral.borderHover;
</script>

<template>
  <!-- One tile of a wizard's review grid (CM view create step 4): uppercase
       label over its value, and clickable when it maps back to a step. -->
  <component
    :is="props.clickable ? 'button' : 'div'"
    class="gaia-summary-tile"
    :class="{ clickable: props.clickable }"
    :type="props.clickable ? 'button' : undefined"
    @click="props.clickable && emit('click')"
  >
    <gaia-text variant="label" size="micro" tone="faint">{{ props.label }}</gaia-text>
    <gaia-text
      class="value"
      size="body"
      :weight="props.empty ? 'regular' : 'semibold'"
      :tone="props.empty ? 'muted' : 'ink'"
    >
      <slot>{{ props.value }}</slot>
    </gaia-text>
  </component>
</template>

<style scoped>
.gaia-summary-tile {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: none;
  font-family: inherit;
  text-align: left;
}

.gaia-summary-tile.clickable {
  cursor: pointer;
}

.gaia-summary-tile.clickable:hover {
  border-color: v-bind(borderHover);
}

.value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
